import React from 'react';
import './TotalPortfolioCard.css';
import './BankruptcyPortfolioCard.css';

export interface BankruptcyMetrics {
  total_bankruptcy_loans: number;
  chapter7_loans: number;
  chapter13_loans: number;
  pct_chapter7: number;
  pct_chapter13: number;
}

interface BankruptcyPortfolioCardProps {
  metrics: BankruptcyMetrics | null;
  loading?: boolean;
  onChapterClick?: (chapter: '7' | '13') => void;
}

const BankruptcyPortfolioCard: React.FC<BankruptcyPortfolioCardProps> = ({
  metrics,
  loading = false,
  onChapterClick,
}) => {
  const formatPercent = (value: number): string => `${value.toFixed(1)}%`;

  return (
    <div className="total-portfolio-card">
      <div className="total-portfolio-header">
        <div className="total-portfolio-icon">
          <span className="portfolio-icon">📘</span>
        </div>
        <div className="total-portfolio-info">
          <h3 className="total-portfolio-name">Total Bankruptcy Portfolio</h3>
          <p className="total-portfolio-subtitle">Chapter 7 / Chapter 13</p>
        </div>
      </div>

      <div className="total-portfolio-metrics">
        {!metrics ? (
          <p style={{ margin: 0, color: '#6b7280' }}>
            {loading ? 'Loading Bankruptcy metrics…' : 'No Bankruptcy data available.'}
          </p>
        ) : (
          <>
            <div className="metric-row">
              <div className="metric-item">
                <span className="metric-label">Total loans</span>
                <span className="metric-value">{metrics.total_bankruptcy_loans.toLocaleString()}</span>
              </div>
            </div>

            <div className="bankruptcy-chapter-boxes">
              <div
                className="bankruptcy-chapter-box"
                role={onChapterClick ? 'button' : undefined}
                tabIndex={onChapterClick ? 0 : undefined}
                onClick={() => onChapterClick?.('7')}
                onKeyDown={(e) => {
                  if (!onChapterClick) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onChapterClick('7');
                  }
                }}
                title="Click to view Chapter 7 accounts"
              >
                <div className="bankruptcy-chapter-box-title">Chapter 7</div>
                <div className="bankruptcy-chapter-box-main">
                  <div className="bankruptcy-chapter-box-pct">{formatPercent(metrics.pct_chapter7)}</div>
                  <div className="bankruptcy-chapter-box-count">{metrics.chapter7_loans.toLocaleString()} loans</div>
                </div>
                <div className="bankruptcy-chapter-box-sub">Open secured / unsecured</div>
              </div>

              <div
                className="bankruptcy-chapter-box"
                role={onChapterClick ? 'button' : undefined}
                tabIndex={onChapterClick ? 0 : undefined}
                onClick={() => onChapterClick?.('13')}
                onKeyDown={(e) => {
                  if (!onChapterClick) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onChapterClick('13');
                  }
                }}
                title="Click to view Chapter 13 accounts"
              >
                <div className="bankruptcy-chapter-box-title">Chapter 13</div>
                <div className="bankruptcy-chapter-box-main">
                  <div className="bankruptcy-chapter-box-pct">{formatPercent(metrics.pct_chapter13)}</div>
                  <div className="bankruptcy-chapter-box-count">{metrics.chapter13_loans.toLocaleString()} loans</div>
                </div>
                <div className="bankruptcy-chapter-box-sub">Open secured / unsecured</div>
              </div>
            </div>
          </>
        )}
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}

      <div className="click-hint">
        <span>Click a chapter to drill into secured / unsecured accounts</span>
      </div>
    </div>
  );
};

export default BankruptcyPortfolioCard;

